<template>
  <v-dialog
    v-if="editedChain"
    :value="dialog"
    :persistent="isModelChanged()"
    :max-width="800"
    content-class="d-dialog"
    scrollable
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
  >
    <v-card outlined class="d-dialog-card">
      <v-card-title>
        {{ chain ? $t('chain.editChain') : $t('chain.createChain') }}
        <d-btn-icon
          type="close"
          btn-class="d-dialog-btn-close"
          tabindex="4"
          @click="escape(closeDialog)"
        ></d-btn-icon>
      </v-card-title>

      <v-divider></v-divider>
      <v-card-text>
        <v-form ref="form" @submit.stop.prevent>
          <v-text-field
            v-if="!isChainAdmin"
            v-model.trim="editedChain.name"
            :rules="rules.name"
            :label="$t('chain.edit.name')"
            tabindex="1"
            clearable
            autofocus
          ></v-text-field>
          <v-select
            v-model="editedChain.dealers"
            :label="$t('chain.edit.dealers')"
            :items="shortDealersSelectItems"
            tabindex="1"
            :autofocus="isChainAdmin"
            multiple
            chips
          ></v-select>
          <!-- Guidelines editor (rich text) -->
          <template v-if="!isChainAdmin">
            <label class="mt-4 v-label guidelines-label">{{ $t('chain.edit.guidelines') }}</label>
            <d-wysiwyg-editor
              :key="editedChain && editedChain.id ? 'guidelines-' + editedChain.id : 'guidelines-new'"
              v-model.trim="editedChain.guidelines"
            ></d-wysiwyg-editor>
          </template>
        </v-form>
      </v-card-text>

      <v-card-actions class="justify-center">
        <v-btn
          v-text="$t('cancel')"
          color="secondary"
          text
          tabindex="3"
          @click="closeDialog"
        ></v-btn>
        <v-btn
          v-text="$t('save')"
          :disabled="!isModelChanged()"
          color="primary"
          text
          tabindex="2"
          @click="submit"
        ></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapActions, mapGetters } from 'vuex'
import { chainSchema } from '@/constants'

import objectUtil from '@/utils/object.util'

import dialogMixin from '@/mixins/dialog.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import errorMixin from '@/mixins/error.mixin'

import DBtnIcon from '@/components/DBtnIcon'
import DWysiwygEditor from '@/components/DWysiwygEditor'
import DOMPurify from 'dompurify'


export default {
  props: {
    chain: Object
  },


  components: {
    DBtnIcon,
    DWysiwygEditor
  },


  mixins: [
    dialogMixin,
    snackbarMixin,
    validationMixin,
    unsavedChangesMixin,
    errorMixin
  ],


  data () {
    return {
      editedChain: null,
      defaultChain: {
        name: '',
        dealers: [],
        guidelines: ''
      },
      existingNames: []
    }
  },

  computed: {
    ...mapGetters('dealer', ['shortDealers', 'shortDealersCurrentChain']),
    ...mapGetters('chain', ['chainById', 'chains']),
    ...mapGetters('user', ['currentUserIsChainAdmin', 'currentUser']),

    isChainAdmin () {
      return this.currentUserIsChainAdmin
    },

    shortDealersSelectItems () {
      const dealers = this.isChainAdmin ? this.shortDealersCurrentChain : this.shortDealers
      return dealers.map(dealer => {
        let text = dealer.name
        if (dealer.chainId) {
          const chainName = this.chainById(dealer.chainId)?.name
          text += ` (${chainName})`
        }
        return {
          text,
          value: dealer.id,
          disabled: dealer.chainId && dealer.chainId !== this.editedChain.id
        }
      })
    },

    rulesToApply () {
      const rule = this.rule

      return {
        name: [
          rule.required(),
          rule.maxLength(chainSchema.nameMaxLength),
          rule.unique(this.existingNames, this.$t('chain.edit.nameExists'))
        ]
      }
    }
  },


  methods: {
    ...mapActions('chain', ['createChain', 'updateChain', 'updateChainDealers']),
    ...mapActions('dealer', ['loadShortDealers', 'loadShortDealersByChainId']),

    onDialogOpen () {
      this.editedChain = objectUtil.getObjectCopy(this.chain || this.defaultChain)

      // ensure guidelines is defined and sanitized for the editor
      this.editedChain.guidelines = DOMPurify.sanitize(this.editedChain.guidelines || '')

      this.initWatchedModel(this.editedChain)
      this.resetValidation()

      this.existingNames = []
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        const chain = objectUtil.getObjectCopy(this.editedChain)

        // sanitize guidelines before sending
        if (chain.guidelines) {
          chain.guidelines = DOMPurify.sanitize(chain.guidelines)
        }

        if (this.isChainAdmin) {
          await this.updateChainDealers({ id: chain.id, dealers: chain.dealers })
          this.showSnackbarSuccess(this.$t('chain.chainUpdated'))
        } else if (chain.id) {
          await this.updateChain(chain)
          this.showSnackbarSuccess(this.$t('chain.chainUpdated'))
        } else {
          await this.createChain(chain)
          this.showSnackbarSuccess(this.$t('chain.chainCreated'))
        }
        if (this.isChainAdmin) {
          this.loadShortDealersByChainId(this.currentUser.chainId)
        } else {
          this.loadShortDealers()
        }
        this.closeDialog()
      } catch (error) {
        if (this.is409ConflictError(error)) {
          this.hideSnackbar()
          this.existingNames.push(this.editedChain.name)
          this.validate(this.rulesToApply)
        } else {
          this.showSnackbarError()
        }
        this.$log.error(error)
      }
    }
  }
}
</script>

<style scoped>
.guidelines-label {
  font-size: 12px;
  color: #000;
}
</style>
